/**
 * Pre-flight validation for a session batch save (HG-3A G3-02b).
 *
 * Why this exists, and why it names every offender rather than the first:
 * migration 213 made the save one RPC call carrying an array of row updates,
 * applied inside a single transaction. One row that the database refuses
 * therefore fails the whole session's save, not just its own competency. An
 * instructor who has captured nine competencies and mistyped the tenth would
 * otherwise fix one name, press save, and be told about the next one.
 *
 * Two classes of problem, deliberately distinguished:
 *
 *   BLOCKING: an override record the database would reject outright, because
 *   ap_override_category_iff_override and ap_override_note_iff_other are per-row
 *   CHECK constraints in migration 248.
 *
 *   WITHHELD: a dimensions group that is started but incomplete. This one is not
 *   a database error, because buildBatchUpdates never sends a partial group
 *   (ap_dimensions_group_together would reject it). It is worse than an error if
 *   left silent: the save would appear to succeed while the instructor's partial
 *   capture quietly failed to persist. So it is surfaced, by name, and the
 *   caller decides whether to block or warn.
 */

import {
  DIMENSION_GROUP_FIELDS,
  dimensionGroupComplete,
  getEffectiveRow,
  type DraftMap,
  type SessionAssessmentRow,
} from './session-assessment-draft';
import { validateOverride } from '@orca360/shared';

/** A row identified the way an instructor would recognise it. */
export interface CaptureProblem {
  assessmentId: string;
  /** Competency code, e.g. FPM. The instructor-facing name for the row. */
  competency: string;
  /** Machine-readable reasons; see validateOverride for the override codes. */
  reasons: string[];
}

export interface SessionCaptureValidation {
  /** True when nothing would be refused by the database. */
  valid: boolean;
  /** Rows whose override record the database would reject. */
  blocking: CaptureProblem[];
  /** Rows whose partial dimensions group would silently not persist. */
  withheld: CaptureProblem[];
}

type CapturedRow = SessionAssessmentRow & { competency?: string | null };

/** True when the instructor has begun a dimensions group but not finished it. */
function dimensionGroupPartial(row: Partial<SessionAssessmentRow>): boolean {
  const started = DIMENSION_GROUP_FIELDS.some((f) => {
    const v = row[f];
    return v !== null && v !== undefined && v !== '';
  });
  return started && !dimensionGroupComplete(row);
}

/**
 * Validate every dirty row before the batch leaves the client.
 *
 * Only dirty rows are examined: an untouched row is already whatever the
 * database accepted last time, and re-validating it would surface historical
 * rows that legitimately carry no dimensions at all.
 */
export function validateSessionCapture<T extends CapturedRow>(
  baselineRows: T[],
  drafts: DraftMap,
  dirtyIds: Set<string>,
): SessionCaptureValidation {
  const byId = new Map(baselineRows.map((r) => [r.id, r]));
  const blocking: CaptureProblem[] = [];
  const withheld: CaptureProblem[] = [];

  for (const id of dirtyIds) {
    const baseline = byId.get(id);
    if (!baseline) continue;

    const effective = getEffectiveRow(baseline, drafts);
    const competency = effective.competency ?? baseline.competency ?? id;

    const override = validateOverride({
      recordedGrade: effective.score ?? null,
      proposedGrade: effective.proposed_grade ?? null,
      category: (effective.override_category ?? null) as never,
      note: effective.override_note ?? null,
    });

    if (!override.valid) {
      blocking.push({ assessmentId: id, competency, reasons: override.errors });
    }

    if (dimensionGroupPartial(effective)) {
      withheld.push({
        assessmentId: id,
        competency,
        reasons: ['DIMENSION_GROUP_INCOMPLETE'],
      });
    }
  }

  // Stable, instructor-friendly ordering: by competency code, not by the
  // iteration order of a Set.
  const byCompetency = (a: CaptureProblem, b: CaptureProblem) =>
    a.competency.localeCompare(b.competency);
  blocking.sort(byCompetency);
  withheld.sort(byCompetency);

  return { valid: blocking.length === 0 && withheld.length === 0, blocking, withheld };
}

/**
 * One sentence naming every affected competency, for the save control.
 * Never truncates the list: the whole point is that the instructor fixes all of
 * them before the next attempt, because the batch is one transaction.
 */
export function describeCaptureProblems(validation: SessionCaptureValidation): string | null {
  const parts: string[] = [];

  if (validation.blocking.length > 0) {
    const names = validation.blocking.map((p) => p.competency).join(', ');
    parts.push(
      validation.blocking.length === 1
        ? `${names} has an incomplete override record. A category is required on every override, and a note only for “other”.`
        : `These competencies have incomplete override records: ${names}. A category is required on every override, and a note only for “other”.`,
    );
  }

  if (validation.withheld.length > 0) {
    const names = validation.withheld.map((p) => p.competency).join(', ');
    parts.push(
      validation.withheld.length === 1
        ? `${names} has a partly recorded assessment. All three dimensions and the limiting factor are saved together, so finish or clear it.`
        : `These competencies are partly recorded: ${names}. All three dimensions and the limiting factor are saved together, so finish or clear them.`,
    );
  }

  if (parts.length === 0) return null;
  // The whole session saves in one transaction, so one bad row stops all of
  // them. Say so, once, rather than per problem.
  return `${parts.join(' ')} The session saves in one step, so nothing is saved until these are resolved.`;
}
